/**
 * CardElement - Self-contained card input widget
 *
 * Provides a secure, customizable card input interface that developers
 * can embed in their checkout UI. Handles card collection, validation,
 * tokenization, and payment completion.
 *
 * IMPORTANT: This widget assumes payment was already created with all billing
 * information via the /server/payment endpoint. It only collects card details.
 */

import type {
  CardElementOptions,
  CardElementState,
  CardData,
  PaymentResult,
  PaymentError,
  SDKConfig,
  PaymentStatus,
} from '../types';
import type { InternalSDKConfig } from './card-element/types';
import { CardElementRenderer } from './card-element/renderer';
import { CardValidator } from './card-element/validator';
import { PaymentClient } from './card-element/payment-client';
import { UIController } from './card-element/ui-controller';
import { detectBrowserLocale } from '../i18n';

// Internal options type with required defaults
type InternalCardElementOptions = Required<CardElementOptions>;

export class CardElement {
  private container: HTMLElement;
  private options: InternalCardElementOptions;
  private config: InternalSDKConfig;
  private state: CardElementState;
  private mounted: boolean = false;

  // Module instances
  private renderer: CardElementRenderer;
  private validator: CardValidator;
  private paymentClient: PaymentClient;
  private uiController: UIController;

  constructor(config: SDKConfig & { environment: 'sandbox' | 'production' | 'development' }, options: CardElementOptions) {
    const environment = config.environment;
    const locale = config.locale || detectBrowserLocale();

    // Store config for API calls with defaults
    this.config = {
      apiKey: config.apiKey,
      environment,
      apiUrl: PaymentClient.getDefaultApiUrl(environment),
      tokenizationProxyUrl: PaymentClient.getDefaultTokenizationProxyUrl(environment),
      tokenizationProxyKey: PaymentClient.getDefaultTokenizationProxyKey(environment, config.apiKey),
      timeout: config.timeout || 30000,
      debug: config.debug || false,
      locale,
    };

    // Resolve container
    let containerElement: HTMLElement | null;
    if (typeof options.container === 'string') {
      containerElement = document.querySelector(options.container);
      if (!containerElement) {
        throw new Error(`Container not found: ${options.container}`);
      }
    } else {
      containerElement = options.container;
    }
    this.container = containerElement;

    // Set defaults
    this.options = {
      container: containerElement,
      paymentId: options.paymentId,
      style: options.style || {},
      buttonText: options.buttonText || 'Complete Payment',
      buttonStyle: options.buttonStyle || {},
      placeholders: {
        cardNumber: options.placeholders?.cardNumber || '4242 4242 4242 4242',
        expiry: options.placeholders?.expiry || 'MM/YY',
        cvc: options.placeholders?.cvc || 'CVC',
      },
      onChange: options.onChange || (() => {}),
      onComplete: options.onComplete || (() => {}),
      onError: options.onError || (() => {}),
    };

    // Initialize state
    this.state = {
      complete: false,
      empty: true,
      errors: {},
    };

    // Initialize modules
    this.renderer = new CardElementRenderer(this.container, this.options, locale);
    this.validator = new CardValidator();
    this.paymentClient = new PaymentClient(
      this.config,
      this.options.paymentId
    );
    this.uiController = new UIController(this.renderer);
  }

  /**
   * Mount the element into the DOM
   */
  mount(): void {
    if (this.mounted) {
      throw new Error('CardElement is already mounted');
    }

    // Create shadow root first by calling render(), then inject styles
    this.renderer.render();
    this.renderer.injectDefaultStyles();
    this.setupEventListeners();
    this.applyStyles();

    // Set initial button state (disabled when fields are empty)
		this.uiController.updateButtonState();
    
    this.mounted = true;
  }


  /**
   * Submit payment (tokenizes card and confirms payment)
   * Payment must already be created with billing information
   */
  async submit(): Promise<PaymentResult> {
    this.uiController.clearMessages();
    this.uiController.setButtonLoading(true);

    try {
      // Validate card inputs
      if (!this.state.complete) {
        const validationError: PaymentError = {
          code: 'VALIDATION_ERROR',
          message: 'Please complete all card fields.',
          retryable: true,
        };
        this.options.onError(validationError);
        this.uiController.showGeneralError(validationError.message);
        this.uiController.setButtonLoading(false);
        return { status: 'PAYMENT_FAILED' as PaymentStatus, error: validationError };
      }

      // Collect card data
      const cardData: CardData = {
        number: this.getCardNumber(),
        expiration_month: this.getExpiryMonth(),
        expiration_year: this.getExpiryYear(),
        cvc: this.getCvc(),
      };

      // Complete payment using payment client
      const result = await this.paymentClient.completePayment(cardData);

      // Handle errors in the result (from payment client)
      if (result.error) {
        this.options.onError(result.error);
        this.uiController.showGeneralError(result.error.message);
        this.uiController.setButtonLoading(false);
        return result;
      }

      // Success - show success message and trigger callback
      const successMessage = result.alreadyProcessed
        ? 'This payment was already processed successfully.'
        : 'Payment successful!';
      this.uiController.showGeneralSuccess(successMessage);
      this.uiController.setButtonLoading(false);
      this.options.onComplete(result);
      return result;
    } catch (error) {
      // This catch is for unexpected errors (payment client returns PaymentResult, not throws)
      const paymentError: PaymentError = {
        code: 'PAYMENT_FAILED',
        message: error instanceof Error ? error.message : 'Payment failed. Please try again.',
        retryable: true,
      };

      this.options.onError(paymentError);
      this.uiController.showGeneralError(paymentError.message);
      this.uiController.setButtonLoading(false);

      // Return error result instead of throwing
      return { status: 'PAYMENT_FAILED' as PaymentStatus, error: paymentError };
    } finally {
      this.uiController.setButtonLoading(false);
    }
  }

  /**
   * Destroy the element and clean up
   */
  destroy(): void {
    if (this.mounted) {
      // Clear shadow root if it exists
      const shadowRoot = this.container.shadowRoot;
      if (shadowRoot) {
        shadowRoot.innerHTML = '';
      }
      // Reset renderer's shadow root reference
      this.renderer.resetShadowRoot();
      this.mounted = false;
    }
  }

  // ============================================================================
  // Private: Event Handling
  // ============================================================================

  private setupEventListeners(): void {
    this.uiController.setupEventListeners(
      () => this.validateAndUpdate(),
      () => this.validateAndUpdate(),
      () => this.validateAndUpdate(),
      async () => {
        await this.submit();
      }
    );
  }

  // ============================================================================
  // Private: Validation
  // ============================================================================

  private validateAndUpdate(): void {
    const cardNumber = this.getCardNumber();
    const expiry = this.renderer.getInput('inflowpay-expiry').value;
    const cvc = this.getCvc();

    // Hide general error when user starts typing
    this.uiController.hideGeneralError();

    // Hide general success when user starts typing
		this.uiController.hideGeneralSuccess();

    // Validate each field using validator
    const cardNumberError = this.validator.validateCardNumber(cardNumber);
    const expiryError = this.validator.validateExpiry(expiry);
    const cvcError = this.validator.validateCvc(cvc);

    // Update state
    this.state.errors = {
      cardNumber: cardNumberError || undefined,
      expiry: expiryError || undefined,
      cvc: cvcError || undefined,
    };

    this.state.empty = !cardNumber && !expiry && !cvc;
    this.state.complete = !cardNumberError && !expiryError && !cvcError && !this.state.empty;

    // Show only one error at a time (priority: card number > expiry > cvc)
    // Hide all errors first
    this.uiController.showError('card-number', null);
    this.uiController.showError('expiry', null);
    this.uiController.showError('cvc', null);

    // Show the first error found
    if (cardNumberError) {
      this.uiController.showError('card-number', cardNumberError);
    } else if (expiryError) {
      this.uiController.showError('expiry', expiryError);
    } else if (cvcError) {
      this.uiController.showError('cvc', cvcError);
    }

    // Update button state based on errors and completion (after validation and error display)
		this.uiController.updateButtonState();

    // Trigger onChange callback
    this.options.onChange(this.state);
  }

  // ============================================================================
  // Private: Styling
  // ============================================================================

  private applyStyles(): void {
    // Apply custom input styles
    if (this.options.style) {
      this.renderer.applyCustomStyles(this.options.style);
    }

    // Apply custom button styles
    if (this.options.buttonStyle) {
      this.renderer.applyButtonStyles(this.options.buttonStyle);
    }
  }

  // ============================================================================
  // Private: Helpers
  // ============================================================================

  private getCardNumber(): string {
    return this.renderer.getInput('inflowpay-card-number').value.replace(/\s/g, '');
  }

  private getExpiryMonth(): string {
    const expiry = this.renderer.getInput('inflowpay-expiry').value;
    return expiry.split('/')[0] || '';
  }

  private getExpiryYear(): string {
    const expiry = this.renderer.getInput('inflowpay-expiry').value;
    const year = expiry.split('/')[1] || '';
    return year ? '20' + year : '';
  }

  private getCvc(): string {
    return this.renderer.getInput('inflowpay-cvc').value;
  }
}
